/* eslint-disable max-lines */
import { saveCustomer } from 'actions';
import ToggleButton from 'components/ToggleButton';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getCustomer } from 'selectors/auth';
import { useSelector } from 'store';
import SyncDatePickerRow from './SyncDatePickerRow';
import {
  CustomerConfigWithInsightFields,
  formatCommaSeparatedList,
  isAnyO365ScrapeEnabled,
  normalizeNullableDate,
  parseCommaSeparatedList,
  SettingRow,
  SettingsCardHeader,
  TextInputRow,
} from './CustomerEditO365SettingsShared';

const CustomerEditO365InsightSettings: React.FC = () => {
  const dispatch = useDispatch();
  const customer = useSelector(getCustomer);
  const config = customer?.config as CustomerConfigWithInsightFields | undefined;

  const [enableOutlookScrape, setEnableOutlookScrape] = useState(false);
  const [enableOutlookBigBackfillScrape, setEnableOutlookBigBackfillScrape] = useState(false);
  const [maxUserBatchSize, setMaxUserBatchSize] = useState('250');
  const [maxConcurrentOutlookScrape, setMaxConcurrentOutlookScrape] = useState('100');
  const [insightFolders, setInsightFolders] = useState('');
  const [nestedInsightFolders, setNestedInsightFolders] = useState('');
  const [outlookScrapeAllFoldersForSent, setOutlookScrapeAllFoldersForSent] = useState(false);
  const [tagIncomingMails, setTagIncomingMails] = useState(false);
  const [onlyCustodianSenders, setOnlyCustodianSenders] = useState(false);
  const [useInternetIdAsPlatformGuid, setUseInternetIdAsPlatformGuid] = useState(false);
  const [enableSubscriptions, setEnableSubscriptions] = useState(false);
  const [syncStartDate, setSyncStartDate] = useState('');
  const [syncEndDate, setSyncEndDate] = useState('');

  const resetFromCustomer = (): void => {
    if (!config) return;

    const insightConfig = config.o365_config ?? {};
    setEnableOutlookScrape(insightConfig.enable_outlook_scrape ?? false);
    setEnableOutlookBigBackfillScrape(insightConfig.enable_outlook_big_backfill_scrape ?? false);
    setMaxUserBatchSize(String(insightConfig.max_user_batch_size ?? 250));
    setMaxConcurrentOutlookScrape(String(insightConfig.max_concurrent_outlook_scrape ?? 100));
    setInsightFolders(formatCommaSeparatedList(insightConfig.insight_folders));
    setNestedInsightFolders(formatCommaSeparatedList(insightConfig.nested_insight_folders));
    setOutlookScrapeAllFoldersForSent(insightConfig.outlook_scrape_all_folders_for_sent ?? false);
    setTagIncomingMails(insightConfig.tag_incoming_mails ?? false);
    setOnlyCustodianSenders(insightConfig.only_custodian_senders ?? false);
    setUseInternetIdAsPlatformGuid(insightConfig.use_internet_id_as_platform_guid ?? false);
    setEnableSubscriptions(insightConfig.enable_subscriptions ?? false);
    setSyncStartDate(config.sync_start_date ?? '');
    setSyncEndDate(config.sync_end_date ?? '');
  };

  useEffect(() => {
    if (!config) return;
    resetFromCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const handleSave = (): void => {
    if (!config) return;

    const batchSize = Number.parseInt(maxUserBatchSize, 10);
    const concurrentScrape = Number.parseInt(maxConcurrentOutlookScrape, 10);
    if (
      Number.isNaN(batchSize) ||
      batchSize < 1 ||
      Number.isNaN(concurrentScrape) ||
      concurrentScrape < 1
    ) {
      return;
    }

    dispatch(
      saveCustomer({
        config: {
          ...config,
          sync_start_date: normalizeNullableDate(syncStartDate),
          sync_end_date: normalizeNullableDate(syncEndDate),
          o365_config: {
            ...(config.o365_config ?? {}),
            // @ts-ignore
            enable_outlook_scrape: enableOutlookScrape,
            enable_outlook_big_backfill_scrape: enableOutlookBigBackfillScrape,
            max_user_batch_size: batchSize,
            max_concurrent_outlook_scrape: concurrentScrape,
            insight_folders: parseCommaSeparatedList(insightFolders),
            nested_insight_folders: parseCommaSeparatedList(nestedInsightFolders),
            outlook_scrape_all_folders_for_sent: outlookScrapeAllFoldersForSent,
            tag_incoming_mails: tagIncomingMails,
            only_custodian_senders: onlyCustodianSenders,
            use_internet_id_as_platform_guid: useInternetIdAsPlatformGuid,
            enable_subscriptions: enableSubscriptions,
          },
        },
      })
    );
  };

  const hasChanges = (): boolean => {
    if (!config) return false;

    const insightConfig = config.o365_config ?? {};
    const batchSize = Number.parseInt(maxUserBatchSize, 10);
    const concurrentScrape = Number.parseInt(maxConcurrentOutlookScrape, 10);

    if ((insightConfig.enable_outlook_scrape ?? false) !== enableOutlookScrape) return true;
    if (
      (insightConfig.enable_outlook_big_backfill_scrape ?? false) !== enableOutlookBigBackfillScrape
    ) {
      return true;
    }
    if ((insightConfig.max_user_batch_size ?? 250) !== batchSize) return true;
    if ((insightConfig.max_concurrent_outlook_scrape ?? 100) !== concurrentScrape) return true;
    if (
      JSON.stringify(parseCommaSeparatedList(insightFolders)) !==
      JSON.stringify(insightConfig.insight_folders ?? [])
    ) {
      return true;
    }
    if (
      JSON.stringify(parseCommaSeparatedList(nestedInsightFolders)) !==
      JSON.stringify(insightConfig.nested_insight_folders ?? [])
    ) {
      return true;
    }
    if (
      (insightConfig.outlook_scrape_all_folders_for_sent ?? false) !==
      outlookScrapeAllFoldersForSent
    ) {
      return true;
    }
    if ((insightConfig.tag_incoming_mails ?? false) !== tagIncomingMails) return true;
    if ((insightConfig.only_custodian_senders ?? false) !== onlyCustodianSenders) return true;
    if ((insightConfig.use_internet_id_as_platform_guid ?? false) !== useInternetIdAsPlatformGuid) {
      return true;
    }
    if ((insightConfig.enable_subscriptions ?? false) !== enableSubscriptions) return true;
    if ((config.sync_start_date ?? '') !== syncStartDate) return true;
    if ((config.sync_end_date ?? '') !== syncEndDate) return true;

    return false;
  };

  const isFormValid = (): boolean => {
    const batchSize = Number.parseInt(maxUserBatchSize, 10);
    const concurrentScrape = Number.parseInt(maxConcurrentOutlookScrape, 10);

    return (
      !Number.isNaN(batchSize) &&
      batchSize >= 1 &&
      !Number.isNaN(concurrentScrape) &&
      concurrentScrape >= 1
    );
  };

  if (!config) {
    return null;
  }

  return (
    <div className="mt-6 mb-8 bg-white border border-litlingo-gray-2 rounded">
      <SettingsCardHeader
        title="Insight settings"
        description=""
        saveTestId="o365-insight-save-button"
        hasChanges={hasChanges()}
        isValid={isFormValid()}
        onCancel={resetFromCustomer}
        onSave={handleSave}
      />

      <SyncDatePickerRow
        label="Sync start date"
        description="Earliest date for Outlook insight sync. Leave unset for no limit."
        value={syncStartDate}
        onChange={setSyncStartDate}
        testId="sync-start-date-input"
      />

      <SyncDatePickerRow
        label="Sync end date"
        description="Latest date for Outlook insight sync. Leave unset for no limit."
        value={syncEndDate}
        onChange={setSyncEndDate}
        testId="sync-end-date-input"
      />

      <SettingRow
        label="Enable Outlook scrape"
        description="Turn on scheduled Outlook mailbox scraping for this customer. Enables subscriptions when turned on; disables them only if no Teams scrape is enabled."
        control={
          <ToggleButton
            dataTestid="enable-outlook-scrape-toggle"
            isOn={enableOutlookScrape}
            handleToggleClick={(): void => {
              setEnableOutlookScrape((value) => {
                const next = !value;
                const insightConfig = config?.o365_config ?? {};
                setEnableSubscriptions(
                  isAnyO365ScrapeEnabled({
                    enableOutlookScrape: next,
                    enableTeamsChatScrape: insightConfig.enable_teams_chat_scrape ?? false,
                    enableTeamsChannelScrape: insightConfig.enable_teams_channel_scrape ?? false,
                  })
                );
                return next;
              });
            }}
          />
        }
      />

      {/* <SettingRow
        label="Enable Outlook big backfill scrape"
        description="Run large historical backfill jobs for Outlook mailboxes."
        control={
          <ToggleButton
            dataTestid="enable-outlook-big-backfill-scrape-toggle"
            isOn={enableOutlookBigBackfillScrape}
            handleToggleClick={(): void => setEnableOutlookBigBackfillScrape((value) => !value)}
          />
        }
      /> */}

      {/* <NumberInputRow
        id="max-user-batch-size"
        label="Max user batch size"
        description="Maximum number of users processed per Outlook scrape batch."
        value={maxUserBatchSize}
        onChange={setMaxUserBatchSize}
        testId="max-user-batch-size-input"
      /> */}

      {/*  <NumberInputRow
        id="max-concurrent-outlook-scrape"
        label="Max concurrent Outlook scrape"
        description="Maximum number of concurrent Outlook scrape workers."
        value={maxConcurrentOutlookScrape}
        onChange={setMaxConcurrentOutlookScrape}
        testId="max-concurrent-outlook-scrape-input"
      /> */}

      <TextInputRow
        id="insight-folders"
        label="Insight folders"
        description="Comma-separated top level mail folder names to scrape."
        value={insightFolders}
        onChange={setInsightFolders}
        testId="insight-folders-input"
        placeholder="sentitems, inbox"
      />

      <TextInputRow
        id="nested-insight-folders"
        label="Nested insight folders"
        description="Comma-separated nested folder paths to scrape."
        value={nestedInsightFolders}
        onChange={setNestedInsightFolders}
        testId="nested-insight-folders-input"
      />

      <SettingRow
        label="Outlook scrape all folders for sent"
        description="Enumerate all mail folders per user for sent-mail ingestion. Ignores explicit insight folder lists."
        control={
          <ToggleButton
            dataTestid="outlook-scrape-all-folders-for-sent-toggle"
            isOn={outlookScrapeAllFoldersForSent}
            handleToggleClick={(): void => setOutlookScrapeAllFoldersForSent((value) => !value)}
          />
        }
      />

      {/* <SettingRow
        label="Tag incoming mails"
        description="Apply tagging to incoming Outlook messages during scrape."
        control={
          <ToggleButton
            dataTestid="tag-incoming-mails-toggle"
            isOn={tagIncomingMails}
            handleToggleClick={(): void => setTagIncomingMails((value) => !value)}
          />
        }
      /> */}

      <SettingRow
        label="Only custodian senders"
        description="Restrict ingestion to messages sent by the custodian."
        control={
          <ToggleButton
            dataTestid="only-custodian-senders-toggle"
            isOn={onlyCustodianSenders}
            handleToggleClick={(): void => setOnlyCustodianSenders((value) => !value)}
          />
        }
      />

      <SettingRow
        label="Use internet ID as platform GUID"
        description="Use the internet message ID as the platform GUID for Outlook messages."
        control={
          <ToggleButton
            dataTestid="use-internet-id-as-platform-guid-toggle"
            isOn={useInternetIdAsPlatformGuid}
            handleToggleClick={(): void => setUseInternetIdAsPlatformGuid((value) => !value)}
          />
        }
      />

      <SettingRow
        label="Enable subscriptions (sent items only)"
        description="Enable Graph webhook subscriptions for sent items folder notifications. Stays on while any Outlook or Teams scrape is enabled."
        control={
          <ToggleButton
            dataTestid="enable-subscriptions-toggle"
            isOn={enableSubscriptions}
            handleToggleClick={(): void => setEnableSubscriptions((value) => !value)}
            disabled={
              !isAnyO365ScrapeEnabled({
                enableOutlookScrape,
                enableTeamsChatScrape: config?.o365_config?.enable_teams_chat_scrape ?? false,
                enableTeamsChannelScrape: config?.o365_config?.enable_teams_channel_scrape ?? false,
              }) && !enableSubscriptions
            }
          />
        }
      />
    </div>
  );
};

export default CustomerEditO365InsightSettings;
